'use client';

import { ChatInput } from '@/@studio-ai/components/chat-input';
import { STUDIO_AI_NAME } from '@/@studio-ai/constants';
import { useElementHeight } from '@/@studio-ai/hooks/useElementHeight';
import { useOrgTrialUsageLimit } from '@/@studio-ai/hooks/useOrgTrialUsageLimit';
import { ASSISTANT_CHAT_INPUT_MESSAGES } from '@/@studio-ai/persistent-assistant/constants';
import { Button, Tooltip } from '@heroui/react';
import { Icon } from '@iconify/react';
import { motion } from 'framer-motion';
import { usePathname } from 'next/navigation';
import { useCallback, useEffect, useMemo } from 'react';
import { PERSISTENT_ASSISTANT_CONFIG } from '../constants';
import { useModalOverlayOpen } from '../hooks/use-modal-overlay-open';
import { ConversationHistoryPopover } from './conversation-history-popover';
import { useFloatingInputBarHeight } from './floating-input-bar-context';

export function FloatingInputBar({
    onSend,
    onClose,
    onOpenPanel,
    selectedText,
}: {
    onSend: (message: string) => void;
    onClose: () => void;
    onOpenPanel: () => void;
    selectedText?: string | null;
}) {
    const pathname = usePathname();
    const { ref, height } = useElementHeight<HTMLDivElement>();
    const { setHeight } = useFloatingInputBarHeight();
    const usageLimit = useOrgTrialUsageLimit();
    const isModalOpen = useModalOverlayOpen();

    useEffect(() => {
        setHeight(height);
    }, [height, setHeight]);

    const placeholder = useMemo(() => {
        if (selectedText) {
            const truncated =
                selectedText.length > 50 ? selectedText.substring(0, 50) + '...' : selectedText;
            return `Ask ${STUDIO_AI_NAME} about "${truncated}"`;
        }
        const pageMatch = Object.entries(ASSISTANT_CHAT_INPUT_MESSAGES.PAGE_PLACEHOLDERS).find(
            ([route]) => pathname === route || pathname.startsWith(`${route}/`),
        );
        if (pageMatch) return pageMatch[1];
        return ASSISTANT_CHAT_INPUT_MESSAGES.DEFAULT_PLACEHOLDER;
    }, [pathname, selectedText]);

    const handleKeyDown = useCallback(
        (e: KeyboardEvent) => {
            // While a modal is open, Escape belongs to the modal — don't also close the bar.
            if (e.key === 'Escape' && !isModalOpen) onClose();
        },
        [onClose, isModalOpen],
    );

    useEffect(() => {
        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [handleKeyDown]);

    return (
        <div
            ref={ref}
            data-persistent-assistant
            className="pointer-events-none fixed inset-x-0 bottom-0 flex justify-center px-4 pb-6"
            style={{
                zIndex: isModalOpen
                    ? PERSISTENT_ASSISTANT_CONFIG.Z_INDEX_UNDER_MODAL
                    : PERSISTENT_ASSISTANT_CONFIG.Z_INDEX,
            }}
        >
            <motion.div
                className="pointer-events-auto flex w-full max-w-3xl items-center gap-2"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 20 }}
                transition={{ duration: 0.2 }}
            >
                <ConversationHistoryPopover
                    placement="top-end"
                    onSelect={onOpenPanel}
                    trigger={
                        <Button
                            isIconOnly
                            aria-label="Conversation history"
                            variant="flat"
                            radius="full"
                            size="sm"
                            className="h-8 w-8 min-w-8 bg-default-100 text-default-400 hover:text-default-600"
                        >
                            <Icon icon="lucide:clock" width={14} height={14} />
                        </Button>
                    }
                />
                <div className="flex-1">
                    <ChatInput
                        onSendMessage={(msg) => onSend(msg)}
                        isLoading={false}
                        placeholder={placeholder}
                        showSmartRoutingToggle={false}
                        usageLimit={usageLimit}
                        compact
                    />
                </div>
                <Tooltip content="Close" size="sm" delay={400}>
                    <Button
                        isIconOnly
                        aria-label="Close"
                        variant="flat"
                        radius="full"
                        size="sm"
                        className="h-8 w-8 min-w-8 bg-default-100 text-default-400 hover:text-default-600"
                        onPress={onClose}
                    >
                        <Icon icon="lucide:x" width={14} height={14} />
                    </Button>
                </Tooltip>
            </motion.div>
        </div>
    );
}
